// Single source of truth for bookable special offers.
// Consumed by SpecialOffers.tsx (the /offers page), SpecialOffersPopup.tsx and
// Enquire.tsx — editing an offer here updates every surface automatically.

import privateDinnerAsset from "@/assets/presidential-private-dinner.jpg.asset.json";
import godsWindowAsset from "@/assets/gods-window-2.jpg.asset.json";
import poolSunsetAsset from "@/assets/pool-sunset.jpg.asset.json";
import champagneAsset from "@/assets/champagne-celebration.jpg.asset.json";
import noFrillsSummerAsset from "@/assets/cheetah-room-view.jpg.asset.json";

export const BOOKING_URL =
  "https://booking.roomraccoon.co.za/zwahili-game-lodge/en/";

export interface Offer {
  slug: string;
  eyebrow: string;
  title: string;
  /** Card price line. Empty string hides the price. */
  price: string;
  validity: string;
  includes: string[];
  footnote?: string;
  /** Short marketing copy shown on the offers page card. */
  description?: string;
  /** Heading above the inclusions list. Supports newlines. */
  includesHeading?: string;
  /** Extra highlighted line below the inclusions (e.g. add-ons). */
  addOn?: string;
  /** Card image + alt text, shared by the offers page and the popup. */
  image: string;
  imageSrcSet?: string;
  imageAlt: string;
  /** Gold badge on the image. */
  badge?: string;
  /** Secondary dark badge on the image. */
  ribbon?: string;
  /** Show the direct RoomRaccoon booking button. */
  bookable: boolean;
  /** Numeric price (ZAR) used for Offer structured data. */
  schemaPrice?: string;
  /** Human unit for the structured-data price, e.g. "per couple for 2 nights". */
  schemaPriceUnit?: string;
  /** Offer category used for structured data. */
  schemaCategory?: string;
}

export const OFFERS: Offer[] = [
  {
    slug: "romantic-bushbreak",
    eyebrow: "Couples Getaway",
    title: "Wildly Romantic Bushbreak",
    price: "From R12,330 per couple for 2 × nights",
    validity: "Valid for travel: 01 September - 10 December 2026.",
    description:
      "Revive, Reconnect.\nEscape the city – a private bush getaway just 2.5hrs North of JHB.\u00a0",
    includes: [
      "Welcome drink on arrival",
      "2 x Nights accommodation",
      "Breakfast, lunch & dinner",
      "1 x 60 minute massage per person",
      "1 x Sunset game drive per person",
      "Romantic turndown",
      "Bottle of sparkling wine",
      "In-room tea/coffee & bottled still water",
      "Wi-Fi at selected hotspots",
    ],
    image: privateDinnerAsset.url,
    imageAlt:
      "Private candlelit dinner on a deck overlooking the bushveld at sunset",
    badge: "ROMANCE",
    ribbon: "MOST POPULAR",
    bookable: true,
    schemaPrice: "12330",
    schemaPriceUnit: "per couple for 2 nights",
    schemaCategory: "Romantic safari getaway",
  },
  {
    slug: "summer-offer",
    eyebrow: "SAFARI ENTHUSIASTS",
    title: "Ultimate Safari Escape",
    price: "From R11,500 for 2 x people for 2 x nights",
    validity: "Valid for travel\u00a0: 01 September - 13 December 2026",
    description: "African safari without the long journey. 2.5 hrs from JHB",
    includes: [
      "Welcome drink on arrival",
      "2 x Nights accommodation",
      "Breakfast, lunch & dinner",
      "1 x Sunset game drive per person",
      "1 x Sunrise guided walk per person",
      "1 x Boma dinner for two\nunder the stars",
      "Self walk hiking trail",
      "In-room tea/coffee & bottled still water",
      "Wi-Fi at selected hotspots",
    ],
    footnote: "Single supplement applies.",
    image: godsWindowAsset.url,
    imageAlt: "Sunset over the bushveld from God's Window at Zwahili",
    badge: "SAFARI ENTHUSIASTS",
    bookable: true,
    schemaPrice: "11500",
    schemaPriceUnit: "for two people for 2 nights",
    schemaCategory: "Safari package",
  },
  {
    slug: "midweek-executive-reset",
    eyebrow: "NO NOISE, NO NOTIFICATIONS, NO TRAFFIC",
    title: "Midweek Executive Reset",
    price: "From R11,480 per couple for 2 x nights",
    validity: "Valid for travel\u00a0: 01 September - 26 November 2026",
    description:
      "Trade the boardroom for the bushveld. Strong coffee & soft silence, just 2 hours north of Johannesburg. 20% off accommodation.",
    includes: [
      "Welcome drink on arrival",
      "2 x Nights accommodation",
      "Breakfast, lunch & dinner",
      "1 x 90 minute full body hot stone massage per person",
      "1 x Sunset game drive per person\u00a0",
      "In-room tea, coffee & bottled water",
      "1 x Guided sunrise walk per person",
      "Room upgrade & late check-out, subject to availability",
    ],
    footnote:
      "Sunday to Thursday stays only, excl. public holidays. Single supplement applies.",
    image: poolSunsetAsset.url,
    imageAlt: "Sunset over the infinity pool and bushveld at Zwahili",
    badge: "MIDWEEK SAVE 20%",
    bookable: true,
    schemaPrice: "11480",
    schemaPriceUnit: "per couple for 2 nights",
    schemaCategory: "Midweek safari break",
  },
  {
    slug: "no-frills-summer",
    eyebrow: "BUILD YOUR OWN PACKAGE",
    title: "No Frills Summer Offer",
    price: "From R2236 per person sharing per night",
    validity: "Valid for travel\u00a0: 01 September 2026 - 31 December 2026",
    description:
      "Build your own package & enjoy the bushveld with exceptional savings.",
    includes: [
      "Welcome drink on arrival",
      "Accommodation",
      "Breakfast & dinner",
      "In-room tea/coffee & bottled still water",
      "Self walk hiking trail",
      "Wi-Fi at selected hotspots",
    ],
    addOn:
      "Add game drives, guided walks, spa treatments & private bushveld picnics at your own pace.",
    footnote: "Single supplement applies.",
    image: noFrillsSummerAsset.url,
    imageAlt:
      "View from a Zwahili suite through billowing curtains onto a private deck with safari chairs overlooking the bushveld",
    badge: "SAVE 28%",
    bookable: false,
    schemaPrice: "2236",
    schemaPriceUnit: "per person sharing per night",
    schemaCategory: "Summer safari special",
  },
  {
    slug: "celebrations-collection",
    eyebrow: "BIRTHDAYS, ANNIVERSARIES, PROPOSALS & MILESTONES",
    title: "Celebrations Collection",
    price: "",
    validity: "Valid all year round as an add-on to an existing accommodation reservation.",
    description:
      "Mark the moment in the bushveld. A boma dinner under the stars with method champagne - a celebration to remember.",
    includesHeading: "Cost R1490 \u00a0 ",
    includes: [
      "Method Champagne",
      "Flower Bouquet",
      "Celebration Cake",
      "Private in room dinner (optional)",
    ],
    addOn: "Add a Couples Private Bushveld Picnic R510pp",
    footnote: "7 days notice required for flowers.",
    image: champagneAsset.url,
    imageAlt:
      "Method cap classique champagne bottle popping to celebrate a birthday, anniversary or proposal at Zwahili Private Game Lodge in Limpopo",
    badge: "CELEBRATE",
    bookable: false,
    schemaPrice: "1490",
    schemaPriceUnit: "per celebration package",
    schemaCategory: "Celebration, anniversary and proposal package",
  },

];

const slugify = (s: string) =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

/** Resolve an offer from a URL param: accepts slug or human title. */
export const findOffer = (raw: string | null | undefined): Offer | undefined => {
  if (!raw) return undefined;
  const key = slugify(raw);
  return OFFERS.find((o) => o.slug === key);
};
